import React, { useEffect, useState } from 'react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { CalendarIcon, ChevronLeftIcon, ChevronRightIcon, ClockIcon, MapPinIcon, UserIcon, UsersIcon, FilterIcon, PlusIcon, SearchIcon, EditIcon, TrashIcon, AlertTriangleIcon } from 'lucide-react';
import { dataService } from '../../services/DataService';
import { TrainingService, ServiceStatus, ServiceType, TrainingType } from '../../models/TrainingService';
import { Project } from '../../models/Project';
import { User, UserRole } from '../../models/User';
import { useNavigate } from 'react-router-dom';
export function PlannerSchedule() {
  const navigate = useNavigate();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState<'day' | 'week' | 'month'>('week');
  const [trainings, setTrainings] = useState<TrainingService[]>([]);
  const [selectedTraining, setSelectedTraining] = useState<string | null>(null);
  const [trainingDetails, setTrainingDetails] = useState<{
    service?: TrainingService;
    project?: Project;
    instructor?: User;
  } | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    instructors: dataService.getUsersByRole(UserRole.INSTRUCTOR).map(inst => inst.id),
    trainingTypes: Object.values(TrainingType),
    serviceTypes: Object.values(ServiceType),
    projects: dataService.getProjects().map(proj => proj.id)
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  // Calculate date range based on view mode
  const getDateRange = () => {
    const today = new Date(currentDate);
    if (viewMode === 'day') {
      const start = new Date(today.setHours(0, 0, 0, 0));
      const end = new Date(today.setHours(23, 59, 59, 999));
      return {
        start,
        end
      };
    } else if (viewMode === 'week') {
      const start = new Date(today);
      start.setDate(today.getDate() - today.getDay() + (today.getDay() === 0 ? -6 : 1)); // Start from Monday
      start.setHours(0, 0, 0, 0);
      const end = new Date(start);
      end.setDate(start.getDate() + 6);
      end.setHours(23, 59, 59, 999);
      return {
        start,
        end
      };
    } else {
      const start = new Date(today.getFullYear(), today.getMonth(), 1);
      const end = new Date(today.getFullYear(), today.getMonth() + 1, 0, 23, 59, 59, 999);
      return {
        start,
        end
      };
    }
  };
  // Load trainings based on date range and filters
  useEffect(() => {
    const {
      start,
      end
    } = getDateRange();
    let services = dataService.getTrainingServicesByDateRange(start, end);
    // Apply filters
    services = services.filter(service => filters.instructors.includes(service.assignedUserId) && filters.trainingTypes.includes(service.trainingType) && filters.serviceTypes.includes(service.serviceType) && filters.projects.includes(service.projectId));
    // Apply search term
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      services = services.filter(service => {
        const project = dataService.getProjectById(service.projectId);
        const instructor = dataService.getUserById(service.assignedUserId);
        return service.description.toLowerCase().includes(searchLower) || service.trainingType.toLowerCase().includes(searchLower) || project?.clientName.toLowerCase().includes(searchLower) || project?.location.city.toLowerCase().includes(searchLower) || instructor?.name.toLowerCase().includes(searchLower);
      });
    }
    setTrainings(services);
  }, [currentDate, viewMode, filters, searchTerm]);
  // Load training details when a training is selected
  useEffect(() => {
    if (selectedTraining) {
      const details = dataService.getFullTrainingDetails(selectedTraining);
      setTrainingDetails(details);
    } else {
      setTrainingDetails(null);
    }
  }, [selectedTraining]);
  // Navigate to previous period
  const goToPrevious = () => {
    const newDate = new Date(currentDate);
    if (viewMode === 'day') {
      newDate.setDate(newDate.getDate() - 1);
    } else if (viewMode === 'week') {
      newDate.setDate(newDate.getDate() - 7);
    } else {
      newDate.setMonth(newDate.getMonth() - 1);
    }
    setCurrentDate(newDate);
  };
  // Navigate to next period
  const goToNext = () => {
    const newDate = new Date(currentDate);
    if (viewMode === 'day') {
      newDate.setDate(newDate.getDate() + 1);
    } else if (viewMode === 'week') {
      newDate.setDate(newDate.getDate() + 7);
    } else {
      newDate.setMonth(newDate.getMonth() + 1);
    }
    setCurrentDate(newDate);
  };
  // Navigate to today
  const goToToday = () => {
    setCurrentDate(new Date());
  };
  // Format date for header
  const formatDateHeader = () => {
    if (viewMode === 'day') {
      return currentDate.toLocaleDateString('nl-NL', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      });
    } else if (viewMode === 'week') {
      const {
        start,
        end
      } = getDateRange();
      return `${start.toLocaleDateString('nl-NL', {
        day: 'numeric',
        month: 'long'
      })} - ${end.toLocaleDateString('nl-NL', {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      })}`;
    } else {
      return currentDate.toLocaleDateString('nl-NL', {
        month: 'long',
        year: 'numeric'
      });
    }
  };
  // Format time
  const formatTime = (date: Date) => {
    return date.toLocaleTimeString('nl-NL', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  // Format date
  const formatDate = (date: Date) => {
    return date.toLocaleDateString('nl-NL', {
      weekday: 'short',
      day: 'numeric',
      month: 'short'
    });
  };
  // Get badge color based on training status
  const getStatusBadge = (status: ServiceStatus) => {
    switch (status) {
      case ServiceStatus.CONFIRMED:
        return <Badge variant="success" data-id="element-2871">Bevestigd</Badge>;
      case ServiceStatus.PLANNED:
        return <Badge variant="warning" data-id="element-2872">Gepland</Badge>;
      case ServiceStatus.IN_PROGRESS:
        return <Badge variant="info" data-id="element-2873">In uitvoering</Badge>;
      case ServiceStatus.COMPLETED:
        return <Badge variant="default" data-id="element-2874">Afgerond</Badge>;
      case ServiceStatus.CANCELLED:
        return <Badge variant="danger" data-id="element-2875">Geannuleerd</Badge>;
      default:
        return <Badge variant="default" data-id="element-2876">Onbekend</Badge>;
    }
  };
  // Handle create new training
  const handleCreateTraining = () => {
    navigate('/training-wizard');
  };
  // Handle edit training
  const handleEditTraining = () => {
    if (selectedTraining) {
      navigate(`/training-wizard?id=${selectedTraining}`);
    }
  };
  // Handle delete training
  const handleDeleteTraining = () => {
    setShowDeleteConfirm(true);
  };
  // Confirm delete training
  const confirmDeleteTraining = () => {
    // In a real app, this would call an API to delete the training
    console.log(`Deleting training ${selectedTraining}`);
    setShowDeleteConfirm(false);
    setSelectedTraining(null);
  };
  // Toggle filters
  const toggleFilters = () => {
    setShowFilters(!showFilters);
  };
  // Apply filters
  const applyFilters = (newFilters: any) => {
    setFilters(newFilters);
    setShowFilters(false);
  };
  // Render day view
  const renderDayView = () => {
    // Group trainings by hour
    const hours = [];
    for (let hour = 7; hour < 19; hour++) {
      const hourTrainings = trainings.filter(training => {
        const trainingHour = new Date(training.startTime).getHours();
        return trainingHour === hour;
      });
      hours.push(<div key={hour} className="flex border-b border-gray-200" data-id="element-2877">
          <div className="w-16 py-2 text-right pr-2 text-xs text-gray-500 border-r border-gray-200" data-id="element-2878">
            {hour}:00
          </div>
          <div className="flex-1 min-h-[60px] relative" data-id="element-2879">
            {hourTrainings.map(training => {
            const startTime = new Date(training.startTime);
            const endTime = new Date(training.endTime);
            const durationHours = (endTime.getTime() - startTime.getTime()) / (1000 * 60 * 60);
            const instructor = dataService.getUserById(training.assignedUserId);
            const project = dataService.getProjectById(training.projectId);
            return <div key={training.id} className={`absolute left-0 right-0 mx-1 p-2 rounded-md border cursor-pointer transition hover:shadow-md ${selectedTraining === training.id ? 'border-[#B61C1C] ring-1 ring-[#B61C1C]' : 'border-gray-200'}`} style={{
              top: `${startTime.getMinutes() / 60 * 100}%`,
              height: `${durationHours * 60}px`,
              backgroundColor: training.status === ServiceStatus.CONFIRMED ? '#f0f9ff' : training.status === ServiceStatus.PLANNED ? '#fffbeb' : training.status === ServiceStatus.IN_PROGRESS ? '#ecfdf5' : training.status === ServiceStatus.COMPLETED ? '#f9fafb' : training.status === ServiceStatus.CANCELLED ? '#fef2f2' : '#f9fafb'
            }} onClick={() => setSelectedTraining(training.id)} data-id="element-2880">
                  <div className="font-medium text-sm truncate" data-id="element-2881">
                    {training.description}
                  </div>
                  <div className="text-xs flex items-center" data-id="element-2882">
                    <ClockIcon className="h-3 w-3 mr-1" data-id="element-2883" />
                    <span data-id="element-2884">
                      {formatTime(startTime)} - {formatTime(endTime)}
                    </span>
                  </div>
                  <div className="text-xs flex items-center" data-id="element-2885">
                    <UserIcon className="h-3 w-3 mr-1" data-id="element-2886" />
                    <span data-id="element-2887">{instructor?.name}</span>
                  </div>
                  <div className="text-xs flex items-center" data-id="element-2888">
                    <MapPinIcon className="h-3 w-3 mr-1" data-id="element-2889" />
                    <span data-id="element-2890">{project?.location.city}</span>
                  </div>
                </div>;
          })}
          </div>
        </div>);
    }
    return <div className="border border-gray-200 rounded-md overflow-hidden" data-id="element-2891">
        {hours}
      </div>;
  };
  // Render week view
  const renderWeekView = () => {
    const {
      start
    } = getDateRange();
    const days = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date(start);
      date.setDate(start.getDate() + i);
      const dayTrainings = trainings.filter(training => {
        const trainingDate = new Date(training.startTime);
        return trainingDate.getDate() === date.getDate() && trainingDate.getMonth() === date.getMonth() && trainingDate.getFullYear() === date.getFullYear();
      }).sort((a, b) => a.startTime.getTime() - b.startTime.getTime());
      const isToday = date.toDateString() === new Date().toDateString();
      days.push(<div key={i} className="min-w-[200px]" data-id="element-2892">
          <div className={`py-2 text-center font-medium border-b ${isToday ? 'bg-[#B61C1C] text-white' : 'bg-gray-50 text-gray-700'}`} data-id="element-2893">
            {date.toLocaleDateString('nl-NL', {
            weekday: 'short',
            day: 'numeric'
          })}
          </div>
          <div className="p-2 space-y-2 h-full min-h-[200px]" data-id="element-2894">
            {dayTrainings.length > 0 ? dayTrainings.map(training => {
            const instructor = dataService.getUserById(training.assignedUserId);
            const project = dataService.getProjectById(training.projectId);
            return <div key={training.id} className={`p-2 border rounded cursor-pointer transition hover:shadow-sm ${selectedTraining === training.id ? 'border-[#B61C1C] bg-red-50' : 'border-gray-200'}`} style={{
              backgroundColor: training.status === ServiceStatus.CONFIRMED ? '#f0f9ff' : training.status === ServiceStatus.PLANNED ? '#fffbeb' : training.status === ServiceStatus.IN_PROGRESS ? '#ecfdf5' : training.status === ServiceStatus.COMPLETED ? '#f9fafb' : training.status === ServiceStatus.CANCELLED ? '#fef2f2' : '#f9fafb'
            }} onClick={() => setSelectedTraining(training.id)} data-id="element-2895">
                    <div className="text-xs font-medium text-gray-900 truncate" data-id="element-2896">
                      {training.description}
                    </div>
                    <div className="flex items-center mt-1 text-xs text-gray-600" data-id="element-2897">
                      <ClockIcon className="h-3 w-3 mr-1" data-id="element-2898" />
                      <span data-id="element-2899">
                        {formatTime(training.startTime)} -{' '}
                        {formatTime(training.endTime)}
                      </span>
                    </div>
                    <div className="flex items-center mt-1 text-xs text-gray-600" data-id="element-2900">
                      <UserIcon className="h-3 w-3 mr-1" data-id="element-2901" />
                      <span className="truncate" data-id="element-2902">{instructor?.name}</span>
                    </div>
                    <div className="flex items-center mt-1 text-xs text-gray-600" data-id="element-2903">
                      <MapPinIcon className="h-3 w-3 mr-1" data-id="element-2904" />
                      <span className="truncate" data-id="element-2905">{project?.location.city}</span>
                    </div>
                  </div>;
          }) : <div className="flex items-center justify-center h-full text-gray-400 text-xs" data-id="element-2906">
                Geen trainingen
              </div>}
          </div>
        </div>);
    }
    return <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-2907">
        <div className="flex overflow-x-auto" data-id="element-2908">{days}</div>
      </div>;
  };
  // Render month view
  const renderMonthView = () => {
    const firstDayOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1).getDay();
    const daysInMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0).getDate();
    const weeks = [];
    let days = [];
    // Adjust first day of week (0 = Sunday, 1 = Monday)
    const adjustedFirstDay = firstDayOfMonth === 0 ? 6 : firstDayOfMonth - 1;
    // Fill in days from previous month
    for (let i = 0; i < adjustedFirstDay; i++) {
      days.push(<div key={`prev-${i}`} className="h-24 bg-gray-50" data-id="element-2909"></div>);
    }
    // Fill in days of current month
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
      const dayTrainings = trainings.filter(training => {
        const trainingDate = new Date(training.startTime);
        return trainingDate.getDate() === date.getDate() && trainingDate.getMonth() === date.getMonth() && trainingDate.getFullYear() === date.getFullYear();
      });
      const isToday = date.toDateString() === new Date().toDateString();
      days.push(<div key={day} className={`h-24 border border-gray-200 p-1 overflow-y-auto ${isToday ? 'bg-blue-50' : 'bg-white'}`} data-id="element-2910">
          <div className={`text-sm font-medium ${isToday ? 'text-blue-600' : ''}`} data-id="element-2911">
            {day}
          </div>
          <div className="space-y-1 mt-1" data-id="element-2912">
            {dayTrainings.slice(0, 3).map(training => <div key={training.id} className="px-1 py-0.5 rounded text-xs truncate bg-red-100 text-red-800 cursor-pointer" onClick={e => {
            e.stopPropagation();
            setSelectedTraining(training.id);
          }} data-id="element-2913">
                {formatTime(training.startTime)}{' '}
                {training.description.substring(0, 15)}
                {training.description.length > 15 ? '...' : ''}
              </div>)}
            {dayTrainings.length > 3 && <div className="text-xs text-gray-500 text-center" data-id="element-2914">
                +{dayTrainings.length - 3} meer
              </div>}
          </div>
        </div>);
      // Start new row after Saturday (or when we reach the end)
      if ((days.length - adjustedFirstDay) % 7 === 0 || day === daysInMonth) {
        // Fill in remaining days from next month
        if (day === daysInMonth) {
          const remainingDays = 7 - days.length % 7;
          if (remainingDays < 7) {
            for (let i = 0; i < remainingDays; i++) {
              days.push(<div key={`next-${i}`} className="h-24 bg-gray-50" data-id="element-2915"></div>);
            }
          }
        }
        weeks.push(<div key={weeks.length} className="grid grid-cols-7 gap-1" data-id="element-2916">
            {days}
          </div>);
        days = [];
      }
    }
    return <div className="space-y-1" data-id="element-2917">
        <div className="grid grid-cols-7 gap-1 mb-1" data-id="element-2918">
          {['Ma', 'Di', 'Wo', 'Do', 'Vr', 'Za', 'Zo'].map(day => <div key={day} className="text-center font-medium text-gray-700 py-1" data-id="element-2919">
              {day}
            </div>)}
        </div>
        {weeks}
      </div>;
  };
  return <div className="space-y-6" data-id="element-2920">
      <div className="flex justify-between items-center" data-id="element-2921">
        <h1 className="text-2xl font-bold text-gray-900" data-id="element-2922">Trainingsplanner</h1>
        <div className="flex space-x-2" data-id="element-2923">
          <Button variant="outline" size="sm" onClick={toggleFilters} data-id="element-2924">
            <FilterIcon className="h-4 w-4 mr-1" data-id="element-2925" />
            Filters
          </Button>
          <Button variant="primary" size="sm" onClick={handleCreateTraining} data-id="element-2926">
            <PlusIcon className="h-4 w-4 mr-1" data-id="element-2927" />
            Nieuwe Training
          </Button>
        </div>
      </div>
      {/* Search and filters */}
      <div className="flex space-x-4" data-id="element-2928">
        <div className="relative flex-1" data-id="element-2929">
          <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} data-id="element-2930" />
          <input type="text" placeholder="Zoek op training, locatie, instructeur..." className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#B61C1C]" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-id="element-2931" />
        </div>
      </div>
      {/* Filters panel */}
      {showFilters && <Card data-id="element-2932">
          <div className="p-4 space-y-4" data-id="element-2933">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-2934">
              {/* Instructors filter */}
              <div data-id="element-2935">
                <h3 className="text-sm font-medium text-gray-700 mb-2" data-id="element-2936">
                  Instructeurs
                </h3>
                <div className="space-y-2 max-h-40 overflow-y-auto" data-id="element-2937">
                  {dataService.getUsersByRole(UserRole.INSTRUCTOR).map(instructor => <div key={instructor.id} className="flex items-center" data-id="element-2938">
                        <input type="checkbox" id={`instructor-${instructor.id}`} checked={filters.instructors.includes(instructor.id)} onChange={() => {
                  const newInstructors = filters.instructors.includes(instructor.id) ? filters.instructors.filter(id => id !== instructor.id) : [...filters.instructors, instructor.id];
                  setFilters({
                    ...filters,
                    instructors: newInstructors
                  });
                }} className="h-4 w-4 text-[#B61C1C] focus:ring-[#B61C1C] border-gray-300 rounded" data-id="element-2939" />
                        <label htmlFor={`instructor-${instructor.id}`} className="ml-2 text-sm text-gray-700" data-id="element-2940">
                          {instructor.name}
                        </label>
                      </div>)}
                </div>
              </div>
              {/* Training types filter */}
              <div data-id="element-2941">
                <h3 className="text-sm font-medium text-gray-700 mb-2" data-id="element-2942">
                  Type Training
                </h3>
                <div className="space-y-2" data-id="element-2943">
                  {Object.values(TrainingType).map(type => <div key={type} className="flex items-center" data-id="element-2944">
                      <input type="checkbox" id={`type-${type}`} checked={filters.trainingTypes.includes(type)} onChange={() => {
                  const newTypes = filters.trainingTypes.includes(type) ? filters.trainingTypes.filter(t => t !== type) : [...filters.trainingTypes, type];
                  setFilters({
                    ...filters,
                    trainingTypes: newTypes
                  });
                }} className="h-4 w-4 text-[#B61C1C] focus:ring-[#B61C1C] border-gray-300 rounded" data-id="element-2945" />
                      <label htmlFor={`type-${type}`} className="ml-2 text-sm text-gray-700" data-id="element-2946">
                        {type}
                      </label>
                    </div>)}
                </div>
              </div>
              {/* Projects filter */}
              <div data-id="element-2947">
                <h3 className="text-sm font-medium text-gray-700 mb-2" data-id="element-2948">
                  Klanten
                </h3>
                <div className="space-y-2 max-h-40 overflow-y-auto" data-id="element-2949">
                  {dataService.getProjects().map(project => <div key={project.id} className="flex items-center" data-id="element-2950">
                      <input type="checkbox" id={`project-${project.id}`} checked={filters.projects.includes(project.id)} onChange={() => {
                  const newProjects = filters.projects.includes(project.id) ? filters.projects.filter(id => id !== project.id) : [...filters.projects, project.id];
                  setFilters({
                    ...filters,
                    projects: newProjects
                  });
                }} className="h-4 w-4 text-[#B61C1C] focus:ring-[#B61C1C] border-gray-300 rounded" data-id="element-2951" />
                      <label htmlFor={`project-${project.id}`} className="ml-2 text-sm text-gray-700" data-id="element-2952">
                        {project.clientName}
                      </label>
                    </div>)}
                </div>
              </div>
            </div>
            <div className="flex justify-end space-x-2 pt-2 border-t border-gray-200" data-id="element-2953">
              <Button variant="outline" size="sm" onClick={() => setShowFilters(false)} data-id="element-2954">
                Annuleren
              </Button>
              <Button variant="primary" size="sm" onClick={() => applyFilters(filters)} data-id="element-2955">
                Filters Toepassen
              </Button>
            </div>
          </div>
        </Card>}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-2956">
        <div className="lg:col-span-2" data-id="element-2957">
          <Card data-id="element-2958">
            <div className="flex justify-between items-center p-4 border-b border-gray-200" data-id="element-2959">
              <div className="flex space-x-2" data-id="element-2960">
                <Button variant="outline" size="sm" onClick={goToPrevious} data-id="element-2961">
                  <ChevronLeftIcon className="h-4 w-4" data-id="element-2962" />
                </Button>
                <Button variant="outline" size="sm" onClick={goToToday} data-id="element-2963">
                  Vandaag
                </Button>
                <Button variant="outline" size="sm" onClick={goToNext} data-id="element-2964">
                  <ChevronRightIcon className="h-4 w-4" data-id="element-2965" />
                </Button>
              </div>
              <h2 className="text-lg font-medium text-gray-900" data-id="element-2966">
                {formatDateHeader()}
              </h2>
              <div className="flex space-x-2" data-id="element-2967">
                <Button variant={viewMode === 'day' ? 'primary' : 'outline'} size="sm" onClick={() => setViewMode('day')} data-id="element-2968">
                  Dag
                </Button>
                <Button variant={viewMode === 'week' ? 'primary' : 'outline'} size="sm" onClick={() => setViewMode('week')} data-id="element-2969">
                  Week
                </Button>
                <Button variant={viewMode === 'month' ? 'primary' : 'outline'} size="sm" onClick={() => setViewMode('month')} data-id="element-2970">
                  Maand
                </Button>
              </div>
            </div>
            <div className="p-4" data-id="element-2971">
              {viewMode === 'day' && renderDayView()}
              {viewMode === 'week' && renderWeekView()}
              {viewMode === 'month' && renderMonthView()}
            </div>
          </Card>
        </div>
        <div className="lg:col-span-1" data-id="element-2972">
          {trainingDetails?.service ? <Card data-id="element-2973">
              <div className="p-4 border-b border-gray-200 flex justify-between items-center" data-id="element-2974">
                <h3 className="font-medium text-gray-900" data-id="element-2975">Training Details</h3>
                {getStatusBadge(trainingDetails.service.status)}
              </div>
              <div className="p-4 space-y-4" data-id="element-2976">
                <div data-id="element-2977">
                  <h4 className="text-lg font-medium text-gray-900" data-id="element-2978">
                    {trainingDetails.service.description}
                  </h4>
                  <p className="text-sm text-gray-500" data-id="element-2979">
                    {trainingDetails.service.trainingType}
                  </p>
                </div>
                <div className="space-y-3" data-id="element-2980">
                  <div className="flex items-start" data-id="element-2981">
                    <CalendarIcon className="h-5 w-5 text-gray-400 mr-2 mt-0.5" data-id="element-2982" />
                    <div data-id="element-2983">
                      <p className="text-sm font-medium text-gray-900" data-id="element-2984">
                        Datum & Tijd
                      </p>
                      <p className="text-sm text-gray-600" data-id="element-2985">
                        {formatDate(trainingDetails.service.startTime)} |{' '}
                        {formatTime(trainingDetails.service.startTime)} -{' '}
                        {formatTime(trainingDetails.service.endTime)}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start" data-id="element-2986">
                    <MapPinIcon className="h-5 w-5 text-gray-400 mr-2 mt-0.5" data-id="element-2987" />
                    <div data-id="element-2988">
                      <p className="text-sm font-medium text-gray-900" data-id="element-2989">
                        Locatie
                      </p>
                      <p className="text-sm text-gray-600" data-id="element-2990">
                        {trainingDetails.project?.location.address},{' '}
                        {trainingDetails.project?.location.postalCode}{' '}
                        {trainingDetails.project?.location.city}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start" data-id="element-2991">
                    <UserIcon className="h-5 w-5 text-gray-400 mr-2 mt-0.5" data-id="element-2992" />
                    <div data-id="element-2993">
                      <p className="text-sm font-medium text-gray-900" data-id="element-2994">
                        Instructeur
                      </p>
                      <p className="text-sm text-gray-600" data-id="element-2995">
                        {trainingDetails.instructor?.name}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start" data-id="element-2996">
                    <UsersIcon className="h-5 w-5 text-gray-400 mr-2 mt-0.5" data-id="element-2997" />
                    <div data-id="element-2998">
                      <p className="text-sm font-medium text-gray-900" data-id="element-2999">Klant</p>
                      <p className="text-sm text-gray-600" data-id="element-3000">
                        {trainingDetails.project?.clientName} (Project:{' '}
                        {trainingDetails.project?.projectNumber})
                      </p>
                    </div>
                  </div>
                  {trainingDetails.service.specialNotes && <div className="flex items-start" data-id="element-3001">
                      <AlertTriangleIcon className="h-5 w-5 text-yellow-500 mr-2 mt-0.5" data-id="element-3002" />
                      <div data-id="element-3003">
                        <p className="text-sm font-medium text-gray-900" data-id="element-3004">
                          Bijzonderheden
                        </p>
                        <p className="text-sm text-gray-600" data-id="element-3005">
                          {trainingDetails.service.specialNotes}
                        </p>
                      </div>
                    </div>}
                </div>
                <div className="pt-3 border-t border-gray-200" data-id="element-3006">
                  <div className="flex space-x-2" data-id="element-3007">
                    <Button variant="outline" size="sm" onClick={handleEditTraining} data-id="element-3008">
                      <EditIcon className="h-4 w-4 mr-1" data-id="element-3009" />
                      Bewerken
                    </Button>
                    <Button variant="outline" size="sm" className="text-red-600 border-red-300 hover:bg-red-50" onClick={handleDeleteTraining} data-id="element-3010">
                      <TrashIcon className="h-4 w-4 mr-1" data-id="element-3011" />
                      Verwijderen
                    </Button>
                  </div>
                </div>
              </div>
            </Card> : <Card data-id="element-3012">
              <div className="p-6 text-center" data-id="element-3013">
                <CalendarIcon className="h-12 w-12 text-gray-300 mx-auto mb-2" data-id="element-3014" />
                <h3 className="text-lg font-medium text-gray-900" data-id="element-3015">
                  Geen training geselecteerd
                </h3>
                <p className="text-sm text-gray-500 mt-1" data-id="element-3016">
                  Selecteer een training om details te bekijken
                </p>
              </div>
            </Card>}
        </div>
      </div>
      {/* Delete confirmation modal */}
      {showDeleteConfirm && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-id="element-3017">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6" data-id="element-3018">
            <h3 className="text-lg font-medium text-gray-900 mb-4" data-id="element-3019">
              Training verwijderen
            </h3>
            <p className="text-gray-600 mb-6" data-id="element-3020">
              Weet je zeker dat je deze training wilt verwijderen? Deze actie
              kan niet ongedaan worden gemaakt.
            </p>
            <div className="flex justify-end space-x-3" data-id="element-3021">
              <Button variant="outline" onClick={() => setShowDeleteConfirm(false)} data-id="element-3022">
                Annuleren
              </Button>
              <Button variant="outline" className="text-red-600 border-red-300 hover:bg-red-50" onClick={confirmDeleteTraining} data-id="element-3023">
                Verwijderen
              </Button>
            </div>
          </div>
        </div>}
    </div>;
}